/**
 * Store actions for link operations.
 *
 * Integrates linkBranchesCommand into the Zustand store state management.
 */

import type { WorkflowGraph } from '@/graph';
import type { Store } from '../types';
import { linkBranches, LinkBranchesError } from '@/commands/linkBranchesCommand';
import { normalizeCreationOrders } from '@/commands/helpers/renumberCreationOrders';
import { LINK_EDGE_STYLE } from '@/graph/styles';

type SetState = (partial: Partial<Store> | ((state: Store) => Partial<Store>)) => void;
type GetState = () => Store;

export function createLinkActions(
  set: SetState,
  get: GetState,
  getGraph: () => WorkflowGraph | null | undefined,
) {
  return {
    /**
     * Link two disconnected workflow branches.
     *
     * Validates endpoints, prevents cycles, and renumbers creationOrder.
     * Marks the graph as dirty on success.
     *
     * Throws LinkBranchesError if validation fails.
     */
    linkMaterialBranches(sourceMaterialId: string, targetMaterialId: string): void {
      const state = get();
      try {
        // Create a complete mutable copy of graphData for the command to mutate
        const graphDataCopy = {
          ...state.graphData,
          materialNodes: { ...state.graphData.materialNodes },
          operationNodes: { ...state.graphData.operationNodes },
          flowEdges: { ...state.graphData.flowEdges },
        };

        const result = linkBranches(graphDataCopy, {
          sourceMaterialId,
          targetMaterialId,
        });

        // Update store with mutated graphData (new references ensure Zustand detects changes)
        set({
          graphData: { ...graphDataCopy },
          ui: { ...state.ui, isDirty: true },
        });

        
        // Update graph visualization by inserting the link edge directly
        // Use the actual edge direction from the command (leaf → root)
        const graph = getGraph();
        if (graph) {
          const model = graph.getDataModel();
          const sourceCell = model.getCell(result.edgeSourceMaterialId);
          const targetCell = model.getCell(result.edgeTargetMaterialId);
          
          if (sourceCell && targetCell) {
            // Insert the link edge with the special style
            graph.batchUpdate(() => {
              const edge = graph.insertEdge({
                source: sourceCell,
                target: targetCell,
                id: result.linkEdgeId,
                style: LINK_EDGE_STYLE,
                value: '', // Empty string to avoid "[object object]" label
              });
              
              // Store the edgeKind metadata in the cell for persistence during serialization
              // Use a custom property on the cell instead of the value
              (edge as any).edgeKind = 'graphsLink';
            });
          }
        }
      } catch (err) {
        if (err instanceof LinkBranchesError) {
          throw new Error(`Link validation failed: [${err.code}] ${err.message}`);
        }
        throw err;
      }
    },

    /**
     * Remove a link edge and restore original creationOrder spacing.
     *
     * When removing a link, we delete the edge and immediately renormalize
     * creationOrder (0, 1, 2, ..., n-1) to restore proper sequential ordering
     * across all operations.
     */
    removeLink(linkEdgeId: string): void {
      const state = get();
      const edge = state.graphData.flowEdges[linkEdgeId];

      if (!edge) {
        throw new Error(`Link edge ${linkEdgeId} not found`);
      }

      if (edge.edgeKind !== 'graphsLink') {
        throw new Error(`Edge ${linkEdgeId} is not a link edge`);
      }

      set((s) => {
        const flowEdges = { ...s.graphData.flowEdges };
        delete flowEdges[linkEdgeId];

        // After removing the link, renormalize creationOrder for all operations
        // This restores proper sequential ordering for disconnected chains
        const graphData = {
          ...s.graphData,
          flowEdges,
          operationNodes: { ...s.graphData.operationNodes },
        };

        normalizeCreationOrders(graphData);

        return {
          graphData,
          ui: { ...s.ui, isDirty: true },
        };
      });

      // Update graph visualization if available
      const graph = getGraph();
      if (graph) {
        const cell = graph.getDataModel().getCell(linkEdgeId);
        if (cell) {
          graph.getDataModel().remove(cell);
        }
      }

    },
  };
}
